import { useLazyGetNotesQuery } from '@carboncopyco/data';
import {
  type IDemo,
  type IRecording,
  RecordingTypes,
} from '@carboncopyco/types';
import { TabGroupName, TabType } from '@carboncopyco/types/desktop';
import { notify } from '@carboncopyco/ui';
import type { ExternalCloneSession } from '../../shared/cloner/types';
import { generateGroupId, getGroupDefaultValue } from '../../util/tab-util';
import useOpenDemoTab from '../screens/utils/openDemoTab';
import { useClonePreview } from '../screens/utils/useClonePreview';
import { createDemoPreviewSpeakerPanelData } from '../shared/createDemoPreviewSpeakerPanelData';
import { useUser } from '../store/context';
import { useTabManager } from './TabManager';
import { useClonerService } from './cloner/ClonerService';

export function useAppService() {
  const { createGlobalTab } = useTabManager();
  const openDemoTab = useOpenDemoTab();
  const openClonePreview = useClonePreview();
  const { activeOrganization } = useUser();
  const { updateClone } = useClonerService();
  const organizationId = activeOrganization?.id?.toString() || '';
  const [getNotes] = useLazyGetNotesQuery();
  const startClone = (options?: {
    externalCloneSession?: ExternalCloneSession;
    folderId?: string;
  }) => {
    const { externalCloneSession, folderId } = options ?? {};
    createGlobalTab({
      type: TabType.CLONE,
      externalCloneSession,
      organizationId,
      ...(folderId && { folderId }),
    });
  };
  const startUpdateClone = (clone: IRecording) => {
    updateClone(organizationId, clone.id);
  };
  const startNewOverlay = (folderId?: string) => {
    createGlobalTab({
      type: TabType.OVERLAY_NEW,
      ...(folderId && { folderId }),
    });
  };

  const startPreviewDemo = async (demoData: IDemo) => {
    const groupId = generateGroupId(demoData.id);

    const notes = await getNotes({
      organizationId,
      entityId: demoData.id.toString(),
      entityType: 'demo',
    }).unwrap();

    await createDemoPreviewSpeakerPanelData(
      demoData,
      groupId,
      notes?.[0]?.content
    );

    openDemoTab(demoData, {
      tabType:
        demoData.recordingType === RecordingTypes.overlay
          ? TabType.PREVIEW_OVERLAY
          : TabType.PREVIEW_DEMO,
      tabTitle: demoData.demoName,
      showWithoutDrafts: true,
      openFromInfoPage: true,
      group: groupId
        ? getGroupDefaultValue(groupId, TabGroupName.DemoPreview)
        : undefined,
    });
  };

  const startPreviewClone = (clone: IRecording) => {
    try {
      openClonePreview({
        cloneId: clone?.id?.toString() || '',
        createGlobalTab,
        activeOrganization: {
          id: activeOrganization.id.toString(),
        },
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('problem to preview clone', e);
      notify.error('Something went wrong, please try again');
    }
  };

  return {
    startClone,
    startNewOverlay,
    startUpdateClone,
    startPreviewDemo,
    startPreviewClone,
  };
}
